import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { getRuleValue, type RuleOverrides } from '../data/ruleRegistry';
import { fetchRuleOverrides } from '../lib/rulesRepo';
import { RulesContext, type RulesContextValue } from './RulesContext';

// ─── Provider ───────────────────────────────────────────────

export function RulesProvider({ children }: { children: ReactNode }) {
    const [overrides, setOverrides] = useState<RuleOverrides>({});
    const [loading, setLoading] = useState(true);

    const refresh = useCallback(async () => {
        setLoading(true);
        const data = await fetchRuleOverrides();
        setOverrides(data);
        setLoading(false);
    }, []);

    useEffect(() => {
        let active = true;
        (async () => {
            const data = await fetchRuleOverrides();
            if (!active) return;
            setOverrides(data);
            setLoading(false);
        })();

        return () => {
            active = false;
        };
    }, []);

    const getValue = useCallback(
        <T = unknown>(ruleId: string): T => getRuleValue<T>(ruleId, overrides),
        [overrides],
    );

    const value = useMemo<RulesContextValue>(
        () => ({ overrides, loading, refresh, getValue }),
        [overrides, loading, refresh, getValue],
    );

    return (
        <RulesContext.Provider value={value}>
            {children}
        </RulesContext.Provider>
    );
}
